"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";

import CustomImage from "@/components/shared/CustomImage";
import { Typography } from "@/components/shared/Typography";
import { SEARCH_RESULT_NEWS_LIMIT } from "@/data/constants";
import { fetchSearchResult } from "@/services/commonService";
import { ArticleType } from "@/types/article";
import { getNewsDetailsUrl } from "@/utils/getNewsDetailsUrl";
import { getArticleImageUrl, getMediaSrcSet } from "@/utils/getImageUrl";
import SocialShare from "@/components/shared/SocialShare";
import { IoMdShare } from "react-icons/io";
import TitleTag from "@/components/shared/TitleTag";
import { getLabel } from "@/data/labels";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import { getLocalizedString } from "@/utils/getLocalizedString";
import { formatDate } from "@/utils/dateUtils";

interface SearchResultSectionProps {
  initialData: {
    docs: ArticleType[];
    totalDocs: number;
    limit: number;
    page: number;
  };
  searchQuery: string;
  lang: string;
}

export default function SearchResultSection({
  initialData,
  searchQuery,
  lang,
}: SearchResultSectionProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname);

  const [articles, setArticles] = useState<ArticleType[]>(initialData?.docs || []);
  const [filters, setFilters] = useState({
    limit: SEARCH_RESULT_NEWS_LIMIT,
    page: 1,
  });

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ["fetchSearchResult", filters.page, searchQuery],
    enabled: filters.page > 1,
    queryFn: () =>
      fetchSearchResult({ searchQuery, limit: filters?.limit, page: filters?.page, lang }),
  });

  const handleLoadMore = () => {
    setFilters(prev => ({ ...prev, page: prev.page + 1 }));
  };

  useEffect(() => {
    if (data) {
      setArticles(prev => [...prev, ...(data?.data?.docs || [])]);
    }
  }, [data]);

  // Determine if we should show the Load More button
  const showLoadMore =
    articles?.length > 0 &&
    ((filters.page === 1 && initialData.totalDocs > articles.length) ||
      (data && data?.data?.totalDocs > articles.length));

  useEffect(() => {
    setArticles(initialData?.docs || []);
  }, [initialData]);

  return (
    <div>
      {articles?.map(
        (
          { image, title, slug, categoryId, subCategoryId, publishedAt, isLiveNews, isInPrint },
          index,
        ) => {
          return (
            <div
              key={index}
              className="flex flex-row items-start gap-6 pt-6 pb-6 border-b group first:pt-0 "
            >
              <Link
                href={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
                className="w-[30%] min-w-[30%] md:w-[33%] md:min-w-[33%] overflow-hidden rounded-md aspect-[16/12] md:aspect-video"
                aria-label={`View news: ${title}`}
              >
                <CustomImage
                  src={getArticleImageUrl(image, "ld")}
                  srcSet={getMediaSrcSet(image, ["ld", "sd"])}
                  sizes="(max-width: 768px) 30vw, 240px"
                  alt={image?.alt || ""}
                  className="object-cover w-full h-full img-zoom-hover"
                />
              </Link>
              <div className="flex-1">
                <Link
                  href={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
                  aria-label={`View news: ${title}`}
                >
                  <Typography
                    variant="h5"
                    className="line-clamp-3 md:line-clamp-2 font-semibold !text-base md:!text-[18px]"
                  >
                    {isLiveNews && publishedAt && Date.now() - new Date(publishedAt).getTime() <= 12 * 60 * 60 * 1000 && <TitleTag labelKey="LIVE_NEWS" />}
                    {isInPrint && <TitleTag labelKey="PRINT_EDITION" />}
                    {title}
                  </Typography>
                </Link>
                <div className="hidden gap-4 mt-4 uppercase divide-x-2 divide-black md:flex dark:divide-white">
                  <Link
                    href={`/${locale}/${categoryId?.slug}`}
                    aria-label={`View all news in the category: ${
                      getLocalizedString(categoryId?.name, lang) || categoryId?.slug
                    }`}
                  >
                    <Typography className="font-medium" variant="body2">
                      {getLocalizedString(categoryId?.name, lang) || categoryId?.slug}
                    </Typography>
                  </Link>
                  <Link
                    href={`/${locale}/${categoryId?.slug}/${subCategoryId?.slug}`}
                    aria-label={`View all news in the sub-category: ${
                      getLocalizedString(subCategoryId?.name, lang) || subCategoryId?.slug
                    }`}
                  >
                    <Typography
                      className="pl-4 text-secondaryText dark:text-darkBgSecondaryColor"
                      variant="body2"
                    >
                      {getLocalizedString(subCategoryId?.name, lang) || subCategoryId?.slug}
                    </Typography>
                  </Link>
                </div>

                <div className="flex items-center justify-between gap-4 mt-4">
                  <span className="block text-[#858585] text-sm uppercase">
                    {formatDate(publishedAt, lang)}
                  </span>

                  <div className="hidden md:block">
                    <SocialShare
                      title={title}
                      url={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
                    />
                  </div>
                  <div className="md:hidden">
                    <SocialShare
                      title={title}
                      url={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
                      customButton={
                        <button
                          className="flex items-center justify-center text-gray-600 dark:text-white"
                          aria-label="Share"
                        >
                          <IoMdShare />
                        </button>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        },
      )}

      {showLoadMore && (
        <div className="flex items-center justify-center mt-8">
          <button
            className="load-more-btn"
            onClick={handleLoadMore}
            disabled={isFetching}
            aria-label={getLabel(lang, "LOAD_MORE")}
          >
            {getLabel(lang, "LOAD_MORE")}
          </button>
        </div>
      )}
    </div>
  );
}
